import React, { useState } from "react";
import api from "../../api";
import { toast } from "react-toastify";
import { TransactionEditModal } from "./InvestorEditModals";
import { useTranslation } from "react-i18next";
import { FiEdit2, FiTrash2, FiChevronUp, FiChevronDown } from "react-icons/fi";

const ITEMS_PER_PAGE = 10;

const TransactionTable = ({ transactions, onDelete }) => {
  const [page, setPage] = useState(1);
  const [sortBy, setSortBy] = useState("date");
  const [sortOrder, setSortOrder] = useState("desc");
  const [editingTransaction, setEditingTransaction] = useState(null);
  const { t } = useTranslation();

  const handleSort = (column) => {
    if (sortBy === column) {
      setSortOrder(sortOrder === "asc" ? "desc" : "asc");
    } else {
      setSortBy(column);
      setSortOrder("asc");
    }
  };

  const deleteTransaction = async (id) => {
    if (!window.confirm(t("transactions.confirmDelete"))) return;
    try {
      await api.delete(`/transactions/${id}`);
      toast.success(t("transactions.deleted"));
      onDelete();
    } catch {
      toast.error(t("transactions.deleteFailed"));
    }
  };

  const sorted = [...transactions].sort((a, b) => {
    const aVal = a[sortBy] ?? "";
    const bVal = b[sortBy] ?? "";
    if (typeof aVal === "number") {
      return sortOrder === "asc" ? aVal - bVal : bVal - aVal;
    } else {
      return sortOrder === "asc"
        ? String(aVal).localeCompare(String(bVal))
        : String(bVal).localeCompare(String(aVal));
    }
  });

  const pageData = sorted.slice((page - 1) * ITEMS_PER_PAGE, page * ITEMS_PER_PAGE);
  const totalPages = Math.max(1, Math.ceil(sorted.length / ITEMS_PER_PAGE));

  const SortIcon = ({ col }) => {
    if (sortBy !== col) return <span className="inline ml-1 opacity-30 text-xs">⇅</span>;
    return sortOrder === "asc"
      ? <FiChevronUp className="inline ml-1 text-primary text-xs" />
      : <FiChevronDown className="inline ml-1 text-primary text-xs" />;
  };

  return (
    <div className="space-y-3">
      <div className="overflow-x-auto rounded-xl border border-base-200 bg-base-100 shadow-sm">
        <table className="table table-sm">
          <thead className="bg-base-200 border-b border-base-200">
            <tr>
              {["date", "type", "amount", "category"].map((col) => (
                <th
                  key={col}
                  className="text-base-content/50 text-[11px] font-semibold tracking-wider uppercase cursor-pointer select-none hover:text-base-content/70 transition-colors"
                  onClick={() => handleSort(col)}
                >
                  {t(`transactions.${col}`)}
                  <SortIcon col={col} />
                </th>
              ))}
              <th className="text-base-content/50 text-[11px] font-semibold tracking-wider uppercase text-right">
                {t("transactions.actions")}
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-base-200">
            {pageData.map((tx) => (
              <tr key={tx.id} className="hover:bg-base-200/50 transition-colors group">
                <td className="text-base-content/70 tabular-nums">{tx.date}</td>
                <td>
                  <span
                    className={`inline-flex items-center px-2 py-0.5 rounded-md text-xs font-semibold ${
                      tx.type === "deposit"
                        ? "bg-success/10 text-success"
                        : "bg-error/10 text-error"
                    }`}
                  >
                    {t(`transactions.${tx.type}`)}
                  </span>
                </td>
                <td className="text-base-content font-semibold tabular-nums">
                  €{tx.amount.toFixed(2)}
                </td>
                <td className="text-base-content/50">{tx.category || "—"}</td>
                <td>
                  <div className="flex gap-1 justify-end opacity-0 group-hover:opacity-100 transition-opacity">
                    <button
                      onClick={() => setEditingTransaction(tx)}
                      className="w-7 h-7 rounded-lg text-base-content/40 hover:text-base-content hover:bg-base-300 flex items-center justify-center transition-colors"
                      title={t("buttons.edit")}
                    >
                      <FiEdit2 className="text-sm" />
                    </button>
                    <button
                      onClick={() => deleteTransaction(tx.id)}
                      className="w-7 h-7 rounded-lg text-base-content/40 hover:text-error hover:bg-error/10 flex items-center justify-center transition-colors"
                      title={t("buttons.delete")}
                    >
                      <FiTrash2 className="text-sm" />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
            {pageData.length === 0 && (
              <tr>
                <td colSpan="5" className="text-center text-base-content/40 py-10">
                  {t("transactions.noData")}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      <div className="flex justify-center items-center gap-3">
        <button
          disabled={page === 1}
          onClick={() => setPage((p) => p - 1)}
          className="btn btn-ghost btn-sm"
        >
          « {t("transactions.prev")}
        </button>
        <span className="text-sm text-base-content/50">
          {t("transactions.page")} <span className="font-bold text-base-content">{page}</span> / {totalPages}
        </span>
        <button
          disabled={page === totalPages}
          onClick={() => setPage((p) => p + 1)}
          className="btn btn-ghost btn-sm"
        >
          {t("transactions.next")} »
        </button>
      </div>

      {editingTransaction && (
        <TransactionEditModal
          transaction={editingTransaction}
          onClose={() => setEditingTransaction(null)}
          onUpdated={onDelete}
        />
      )}
    </div>
  );
};

export default TransactionTable;
